import { vendorDriverName } from './declarations.ts'
import type { LapDoc } from './timing'

/**
 * Lap-time analysis for one car: which laps count, and what they add up to.
 *
 * All of it pure, and all of it deliberately separate from the screen. These
 * are the numbers somebody makes a decision on during a race — is the new
 * driver quicker, is there time in hand, was that stint actually slow or just
 * neutralised — and being able to test them without rendering anything is worth
 * more here than anywhere else on the dashboard.
 *
 * The definition of a comparable lap has to match timing/internal/store/stints.go
 * exactly. It is applied in two places — the interpreter, when it aggregates a
 * stint, and here — and if they disagree then the average on a stint card and
 * the average on this page differ for the same laps, with nothing on screen to
 * explain why.
 */

/** How a lap may fail to be comparable with a clean one. */
export interface LapFlags {
  inLap: boolean
  outLap: boolean
  /** Any sector run under a full-course yellow or a slow zone. */
  neutralised: boolean
}

export function lapFlags(lap: LapDoc): LapFlags {
  return {
    inLap: !!lap.inLap,
    outLap: !!lap.outLap,
    neutralised:
      !!lap.s1Fcy || !!lap.s2Fcy || !!lap.s3Fcy || !!lap.s1Sz || !!lap.s2Sz || !!lap.s3Sz,
  }
}

/**
 * A lap run at racing speed, start to finish.
 *
 * The same test the interpreter applies when it counts a stint's green laps.
 */
export function isGreenLap(lap: LapDoc): boolean {
  const f = lapFlags(lap)
  return !f.inLap && !f.outLap && !f.neutralised
}

/** What the average is allowed to count. */
export interface AverageOptions {
  /** Pit laps are a different exercise from a flying lap. */
  excludePitLaps: boolean
  /** A lap under a yellow says nothing about pace. */
  excludeNeutralised: boolean
}

export const defaultAverageOptions: AverageOptions = {
  excludePitLaps: true,
  excludeNeutralised: true,
}

export function countsTowardsAverage(lap: LapDoc, options: AverageOptions): boolean {
  if (lap.lapTimeMs == null || lap.lapTimeMs <= 0) return false
  const f = lapFlags(lap)
  if (options.excludePitLaps && (f.inLap || f.outLap)) return false
  if (options.excludeNeutralised && f.neutralised) return false
  return true
}

// ------------------------------------------------------------------ stats

export interface SectorBest {
  ms: number
  lap: number
}

export interface LapStats {
  /** How many laps were on screen when this was computed. */
  considered: number
  /** How many of them the average actually used. */
  counted: number
  averageMs: number | null
  best: { ms: number; lap: number } | null
  sectors: [SectorBest | null, SectorBest | null, SectorBest | null]
  /**
   * The three best sectors added up — a lap nobody drove.
   *
   * Null unless all three exist, because two thirds of an ideal lap is not a
   * time, and showing one would invite it being read against a real lap.
   */
  idealMs: number | null
  /**
   * What the ideal is worth: best actual lap minus ideal.
   *
   * The number people act on. "1:58.4" means little on its own; "0.8s in hand"
   * is a decision about whether to push.
   */
  gainMs: number | null
}

/**
 * Every statistic on the page, from the laps currently displayed.
 *
 * Computed over the FILTERED set, so narrowing to a stint or a driver narrows
 * the numbers with it — which is the point of the filters. The average is
 * narrowed further by its own options, and reports both counts so a total that
 * looks low is explained rather than mysterious.
 */
export function lapStats(laps: readonly LapDoc[], options: AverageOptions): LapStats {
  let sum = 0
  let counted = 0
  let best: { ms: number; lap: number } | null = null
  const sectors: [SectorBest | null, SectorBest | null, SectorBest | null] = [null, null, null]

  for (const lap of laps) {
    if (countsTowardsAverage(lap, options)) {
      sum += lap.lapTimeMs as number
      counted++
    }

    // Best lap is over everything displayed, with no exclusions. A lap that was
    // neutralised or came out of the pits cannot be a personal best in the first
    // place, so filtering here would only ever remove the right answer if the
    // flags were wrong.
    const t = lap.lapTimeMs
    if (t != null && t > 0 && (best == null || t < best.ms)) best = { ms: t, lap: lap.lap }

    // Sectors are judged one at a time, because the flags are per sector. An
    // out-lap has a slow first sector and two ordinary ones, and throwing the
    // whole lap away would discard two perfectly good sector times.
    consider(sectors, 0, lap.s1Ms, lap.s1Fcy || lap.s1Sz, lap.lap)
    consider(sectors, 1, lap.s2Ms, lap.s2Fcy || lap.s2Sz, lap.lap)
    consider(sectors, 2, lap.s3Ms, lap.s3Fcy || lap.s3Sz, lap.lap)
  }

  const idealMs = sectors.every((s) => s !== null)
    ? sectors.reduce((total, s) => total + (s as SectorBest).ms, 0)
    : null

  return {
    considered: laps.length,
    counted,
    averageMs: counted > 0 ? Math.round(sum / counted) : null,
    best,
    sectors,
    idealMs,
    // Only where it is a real comparison. A negative gain would mean the
    // sectors do not sum to the lap — some vendors' do not — and reporting
    // that as time in hand would be worse than reporting nothing.
    gainMs: idealMs != null && best != null && best.ms > idealMs ? best.ms - idealMs : null,
  }
}

function consider(
  into: (SectorBest | null)[],
  index: number,
  ms: number | null | undefined,
  flagged: boolean | undefined,
  lap: number,
): void {
  if (ms == null || ms <= 0 || flagged) return
  const current = into[index]
  if (current == null || ms < current.ms) into[index] = { ms, lap }
}

// ---------------------------------------------------------------- filters

export interface LapFilter {
  /** null shows every stint. */
  stint: number | null
  /**
   * null shows every driver.
   *
   * A resolved name rather than an id, because the two sources of a driver —
   * the team's declaration and the vendor's own field — do not share an
   * identifier, and the filter has to work before anybody has declared
   * anything.
   */
  driver: string | null
}

export const everyLap: LapFilter = { stint: null, driver: null }

/**
 * Narrows the laps to what the controls say.
 *
 * `driverOf` is passed in rather than read off the lap because who was driving
 * is resolved against the team's declarations, and this module must not know
 * about them — the same lap has a different answer for two teams looking at it.
 */
export function applyLapFilter(
  laps: readonly LapDoc[],
  filter: LapFilter,
  driverOf: (lap: LapDoc) => string | null,
): LapDoc[] {
  return laps.filter((lap) => {
    if (filter.stint != null && lap.stintNumber !== filter.stint) return false
    if (filter.driver != null && driverOf(lap) !== filter.driver) return false
    return true
  })
}

/** The stints present in a set of laps, in the order they were run. */
export function stintsInLaps(laps: readonly LapDoc[]): number[] {
  const seen = new Set<number>()
  for (const l of laps) if (l.stintNumber != null) seen.add(l.stintNumber)
  return [...seen].sort((a, b) => a - b)
}

/** The drivers present, as resolved, in the order they first appear. */
export function driversInLaps(
  laps: readonly LapDoc[],
  driverOf: (lap: LapDoc) => string | null,
): string[] {
  const seen: string[] = []
  // Oldest first, so "the order they first appear" is the order they drove
  // rather than the order the table happens to be sorted in.
  for (const lap of [...laps].sort((a, b) => a.lap - b.lap)) {
    const name = driverOf(lap)
    if (name && !seen.includes(name)) seen.push(name)
  }
  return seen
}

/**
 * The vendor's driver name each stint settled on.
 *
 * The per-lap driver field is whoever the timing desk currently has selected,
 * stamped onto whichever lap was completing when the feed was read. At
 * Zandvoort the desk stepped through all three of a car's drivers over the
 * first five laps of the race before landing on the one actually driving, and
 * the two channels disagreed about which lap each wrong name fell on. Read lap
 * by lap that is three drivers in one stint, which cannot happen: a driver
 * change needs a pit stop, and a pit stop starts a new stint.
 *
 * So the name is decided per stint — the one on the most laps, the later
 * one if tied, since the desk corrects itself rather than the reverse — and
 * every lap in the stint reads as that. The same rule the stint chart applies
 * to the interpreter's tally, so the two screens agree. A stint whose laps
 * carry no name at all maps to null, and a lap with no stint number is left
 * to its own field.
 */
export function stintVendorNames(laps: readonly LapDoc[]): Map<number, string | null> {
  const tally = new Map<number, Map<string, { laps: number; last: number }>>()
  for (const l of laps) {
    if (l.stintNumber == null) continue
    const name = vendorDriverName(l.driver)
    const byName = tally.get(l.stintNumber) ?? new Map<string, { laps: number; last: number }>()
    tally.set(l.stintNumber, byName)
    if (!name) continue
    const t = byName.get(name) ?? { laps: 0, last: -1 }
    t.laps++
    t.last = Math.max(t.last, l.lap)
    byName.set(name, t)
  }
  const out = new Map<number, string | null>()
  for (const [stint, byName] of tally) {
    let best: { name: string; laps: number; last: number } | null = null
    for (const [name, t] of byName) {
      if (!best || t.laps > best.laps || (t.laps === best.laps && t.last > best.last)) best = { name, ...t }
    }
    out.set(stint, best?.name ?? null)
  }
  return out
}

export function parseLapFilter(raw: unknown): LapFilter {
  if (!raw || typeof raw !== 'object') return everyLap
  const r = raw as Record<string, unknown>
  return {
    stint: typeof r.stint === 'number' && Number.isFinite(r.stint) ? r.stint : null,
    driver: typeof r.driver === 'string' && r.driver ? r.driver : null,
  }
}

export function parseAverageOptions(raw: unknown): AverageOptions {
  if (!raw || typeof raw !== 'object') return defaultAverageOptions
  const r = raw as Record<string, unknown>
  return {
    excludePitLaps:
      typeof r.excludePitLaps === 'boolean'
        ? r.excludePitLaps
        : defaultAverageOptions.excludePitLaps,
    excludeNeutralised:
      typeof r.excludeNeutralised === 'boolean'
        ? r.excludeNeutralised
        : defaultAverageOptions.excludeNeutralised,
  }
}
